import 'rc-slider/assets/index.css';
import {
  css, type SerializedStyles,
} from '@emotion/react';
import numeral from 'numeral';
import {
  type CSSProperties, forwardRef, useMemo,
} from 'react';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTheme } from '../../../themes/theme.hooks';
import { type ThemeProps } from '../../../types/themeProps';
import {
  SliderComponent, type SliderConstraint, type SliderMarks, type SliderValue,
} from '../slider.component';

const containerWithInlineLabelsStyle = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  paddingBottom: '5px',
});

const containerWithVerticalSliderStyle = css({
  position: 'relative',
  height: '100%',
});

const containerWithLabelsBelowStyle = css({
  position: 'relative',
  paddingBottom: '22px',
});

const commonRangeLabelStyle = css({
  cursor: 'default',
  position: 'absolute',
  transition: 'opacity .2s',
});

const currentValueRightLabelStyle = ({ theme }: ThemeProps) => ({
  color: theme.textColors.tertiary,
  cursor: 'default',
  marginLeft: 12,
});

const currentValueLabelStyle = ({ theme, left }: ThemeProps<{left: number}>) => ({
  left: `${left}%`,
  transform: 'translate(-50%, 2px)',
  zIndex: 1,
  color: theme.textColors.secondary,
});

const labelStyle = (position: 'left' | 'right') => (theme: Theme) => css(commonRangeLabelStyle, {
  color: theme.textColors.tertiary,
  [position]: 0,
});

type SliderWithValuesLabelsProps = Readonly<{
  className?: string;
  constraints?: SliderConstraint | SliderConstraint[];
  currentValueOnTheRight?: boolean;
  delay?: number;
  disabled?: boolean;
  dotStyle?: CSSProperties | ((value: number) => CSSProperties);
  handleLabelStyleCommon?: SerializedStyles;
  included?: boolean;
  labelStyleCommon?: SerializedStyles;
  leftLabelStyle?: SerializedStyles;
  marks?: SliderMarks;
  max?: number;
  min?: number;
  noLabel?: boolean;
  reverse?: boolean;
  sidesLabelStyleCommon?: SerializedStyles;
  step?: number;
  value: SliderValue;
  valuePrefix?: string;
  valueSuffix?: string;
  vertical?: boolean;
  withDots?: boolean;

  labelNumberFormat?: (value: number) => number | string;
  onChange: (value: SliderValue) => void;
}>;

const defaultFormatNumber = (value: number) => numeral(value).format('0a');

export const SliderWithValueLabelsComponent = forwardRef<HTMLDivElement, SliderWithValuesLabelsProps>(({
  className,
  constraints,
  currentValueOnTheRight,
  delay,
  disabled,
  dotStyle,
  handleLabelStyleCommon,
  included,
  labelNumberFormat = defaultFormatNumber,
  labelStyleCommon,
  leftLabelStyle,
  marks,
  max = 100,
  min = 0,
  noLabel,
  onChange,
  reverse,
  sidesLabelStyleCommon,
  step = 1,
  value,
  valuePrefix = '',
  valueSuffix = '',
  vertical,
  withDots,
  ...restProps
}, ref) => {
  const theme = useTheme();

  const valueLabelStyle = value.map(value => currentValueOnTheRight ? css(
    currentValueRightLabelStyle({ theme }),
    labelStyleCommon,
  ) : css(
    commonRangeLabelStyle,
    currentValueLabelStyle({ theme, left: Math.round((value - min) / (max - min) * 100) }),
    labelStyleCommon,
    handleLabelStyleCommon,
  ));

  const compositeLabelStyles = (position: 'left' | 'right') => (theme: Theme) => css(
    labelStyle(position)(theme),
    labelStyleCommon,
    sidesLabelStyleCommon,
    leftLabelStyle,
  );

  const LabelComponent = (
    <>
      {!currentValueOnTheRight && (
        <span css={compositeLabelStyles('left')} >
          {valuePrefix}{labelNumberFormat(min)}{valueSuffix}
        </span>
      )}

      {currentValueOnTheRight && value.map((value, index) => (
        <span
          key={index}
          css={valueLabelStyle[index]}
        >
          {valuePrefix}{labelNumberFormat(value)}{valueSuffix}
        </span>
      ))}

      {!currentValueOnTheRight && (
        <span css={compositeLabelStyles('right')} >
          {valuePrefix}{labelNumberFormat(max)}{valueSuffix}
        </span>
      )}
    </>
  );

  const containerStyle = useMemo(() => {
    if (vertical) {
      return containerWithVerticalSliderStyle;
    }
    if (currentValueOnTheRight || noLabel) {
      return containerWithInlineLabelsStyle;
    }

    return containerWithLabelsBelowStyle;
  }, [currentValueOnTheRight, noLabel, vertical]);

  return (
    <SliderComponent
      {...restProps}
      ref={ref}
      css={[
        containerStyle,
        css({ opacity: disabled ? theme.opacity.disabled : undefined }),
      ]}
      className={className}
      constraints={constraints}
      delay={delay}
      disabled={disabled}
      dotStyle={dotStyle}
      included={included}
      marks={marks}
      max={max}
      min={min}
      onChange={onChange}
      reverse={reverse}
      step={step}
      value={value}
      vertical={vertical}
      withDots={withDots}
    >
      {!noLabel && LabelComponent}
    </SliderComponent>
  );
});
